import { useCallback, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ExternalLink, Settings } from 'lucide-react';
import {
  AppShell,
  RECORD_TYPES,
  SidebarCommandButton,
  WorkspaceMenu,
  type RecordTypeId,
  type WorkspaceMenuLink,
} from '@command-center/ui-system';
import {
  RightSidebarProvider,
  SharedRightSidebar,
  useRightSidebar,
} from '@command-center/oneup-portal-ui';
import { exitBrowserImpersonation } from '@command-center/auth-sdk/browser-impersonation';
import { apiUrl } from './lib/api';
import { AliceDrawer } from './components/alice-drawer';
import { useAuth } from './auth/AuthProvider';
import { CommandPalette } from './components/command-palette';
import { GlobalShortcuts } from './components/global-shortcuts';
import type { AliceContext, AliceLaunch } from './lib/alice-api';
import { getBranding } from './platform/branding';
import { adminConfig } from './platform/config';
import { getNavigationGroups } from './platform/navigation';
import { getRouteTitle } from './platform/modules';
import { VoiceAgentsView } from './views/AiSystemsView';
import { LeadLoopView } from './views/LeadLoopView';
import { LeadsView } from './views/LeadsView';
import { NotFoundView } from './views/NotFoundView';
import { PeopleView } from './views/PeopleView';
import { SettingsView } from './views/SettingsView';
import { AliceFrameworkView } from './views/AliceFrameworkView';
// Supabase-backed Forms + Submissions replace the Airtable Form_Entries source.
import { FormsFeedView } from './views/FormsView';
import { SwitchboardView } from './views/SwitchboardView';

/**
 * One entry, in the account section.
 *
 * The menu used to offer "Portal Settings", "Activity", and "User Settings" —
 * three rows, two of which pointed at the same route. Settings is now a single
 * tabbed destination, so it is a single link, and it sits beside Sign Out
 * because that is where a client looks for "things about me and my portal".
 */
const WORKSPACE_MENU_LINKS: WorkspaceMenuLink[] = [
  { to: '/settings', label: 'Settings', icon: Settings, section: 'account' },
];

export function App() {
  return (
    <RightSidebarProvider storageKey="cascade:right-sidebar">
      <AppContent />
    </RightSidebarProvider>
  );
}

function AppContent() {
  const { user, impersonation, signOut } = useAuth();
  const rightSidebar = useRightSidebar();
  const branding = useMemo(() => getBranding(), []);
  const groups = useMemo(() => getNavigationGroups(), []);
  const { pathname } = useLocation();
  const head = getRouteTitle(pathname);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [aliceSeed, setAliceSeed] = useState<AliceLaunch | null>(null);

  useEffect(() => {
    setAliceSeed(null);
  }, [pathname]);

  const openPalette = useCallback((initialQuery = '') => {
    setPaletteQuery(initialQuery);
    setPaletteOpen(true);
  }, []);
  const openAlice = useCallback((prompt = '', context?: AliceContext, contextLabel?: string) => {
    setAliceSeed({ prompt, context, contextLabel, nonce: Date.now() });
    rightSidebar.showPanel('alice');
  }, [rightSidebar.showPanel]);
  const userName = String(
    user?.user_metadata?.full_name ?? user?.user_metadata?.name ?? '',
  );
  const loadedRecordType = rightSidebar.recordDetails?.recordTypeKey as RecordTypeId | undefined;
  const loadedRecordPresentation = loadedRecordType && loadedRecordType in RECORD_TYPES
    ? RECORD_TYPES[loadedRecordType]
    : undefined;

  const rightSidebarPanel = (
    <SharedRightSidebar
      alice={({ collapse }) => (
        <AliceDrawer
          seed={aliceSeed}
          onCollapse={collapse}
        />
      )}
      recordPresentation={loadedRecordPresentation ? {
        icon: loadedRecordPresentation.icon,
        singularLabel: loadedRecordPresentation.label,
      } : undefined}
    />
  );

  return (
    <>
    {impersonation && (
      <div className="sticky top-0 z-[100] flex flex-wrap items-center justify-between gap-2 border-b border-amber-400/50 bg-amber-100 px-4 py-2 text-sm text-amber-950">
        <strong>
          Viewing as {impersonation.impersonation.targetName} for {impersonation.impersonation.organizationName} — Read Only
        </strong>
        <span>
          {impersonation.impersonation.portalLabel} · expires {new Date(impersonation.impersonation.expiresAt).toLocaleTimeString()}
        </span>
        <button
          type="button"
          className="rounded-md border border-amber-700/30 bg-white px-3 py-1 font-medium"
          onClick={() => void exitBrowserImpersonation(
            apiUrl(''),
            `https://admin.oneuphi.com/users/${encodeURIComponent(impersonation.impersonation.targetAppUserId)}`,
          )}
        >
          Exit Impersonation
        </button>
      </div>
    )}
    <AppShell
      branding={branding}
      groups={groups}
      head={head}
      headerVariant="workspace"
      /* No collapsible nav sections: there are no sections. Five destinations
         in one unlabelled list is the whole navigation. */
      collapsibleSidebar
      sidebarStorageKey={`${adminConfig.appId}:sidebar`}
      sidebarCommand={({ compact }) => (
        <SidebarCommandButton compact={compact} onOpen={() => openPalette()} />
      )}
      sidebarWorkspace={({ compact }) => (
        <WorkspaceMenu
          compact={compact}
          organizationName="Cascade Electric"
          organizationSubtitle="LeadLoop workspace"
          fallbackInitials="CE"
          accountFallbackLabel="Cascade user"
          links={WORKSPACE_MENU_LINKS}
          userName={userName || undefined}
          userEmail={impersonation?.impersonation.targetEmail ?? user?.email}
          onSignOut={() => void signOut()}
        />
      )}
      rightSidebar={rightSidebarPanel}
      mobileRightSidebar
      rightSidebarLabel="Alice, Record Details, and Messages"
      rightSidebarOpenToken={rightSidebar.openToken}
      contentClassName="px-4 pt-4 sm:px-5 sm:pt-5 lg:px-6 lg:pt-6"
      overlays={
        <>
          <GlobalShortcuts openCommandPalette={openPalette} />
          <CommandPalette
            open={paletteOpen}
            initialQuery={paletteQuery}
            onAskAlice={openAlice}
            onOpenChange={setPaletteOpen}
          />
        </>
      }
      sidebarFooter={
        <div className="flex min-w-0 flex-col gap-1.5">
          <a
            href="https://app.oneuphi.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Client Portal in a new tab"
            /* Themed, not hardcoded: this button used to carry One Up's maroon
               (#79141a) inside the Cascade portal. */
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Client Portal
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
          <span className="text-[10px] text-muted-foreground">v0.1.0 · admin</span>
        </div>
      }
    >
      {/*
        LeadLoop is the home. `/dashboard` was the other half of the same
        experience and no longer exists as a page — it redirects rather than
        404s, because it is in browser histories, bookmarks, and the notification
        emails already sent.
      */}
      <Routes>
        <Route path="/" element={<Navigate to="/leadloop" replace />} />
        <Route path="/leadloop" element={<LeadLoopView />} />
        <Route path="/dashboard" element={<Navigate to="/leadloop" replace />} />
        <Route path="/leads" element={<LeadsView />} />
        <Route path="/leads/:id" element={<LeadsView />} />
        {/* People: one destination, two record types, existing URLs intact. */}
        <Route path="/contacts" element={<PeopleView />} />
        <Route path="/contacts/:id" element={<PeopleView />} />
        <Route path="/organizations" element={<PeopleView />} />
        <Route path="/organizations/:id" element={<PeopleView />} />
        <Route path="/people" element={<Navigate to="/contacts" replace />} />
        <Route path="/forms-feed" element={<FormsFeedView />} />
        {/* Legacy Airtable-era paths keep working. */}
        <Route path="/submissions" element={<Navigate to="/forms-feed" replace />} />
        <Route path="/forms" element={<Navigate to="/forms-feed?tab=forms" replace />} />
        <Route path="/ai-systems/voice-agents" element={<VoiceAgentsView />} />
        <Route
          path="/ai-systems/agents"
          element={<Navigate to="/ai-systems/voice-agents?tab=agents" replace />}
        />
        <Route
          path="/ai-systems/calls"
          element={<Navigate to="/ai-systems/voice-agents" replace />}
        />
        {/* Settings absorbed Emails and Activity; both keep working as links. */}
        <Route path="/settings" element={<SettingsView />} />
        <Route path="/settings/alice" element={<AliceFrameworkView />} />
        <Route path="/activity" element={<Navigate to="/settings?tab=activity" replace />} />
        <Route path="/emails" element={<Navigate to="/settings?tab=emails" replace />} />
        <Route path="/emails/:id" element={<LegacyEmailRedirect />} />
        <Route path="/switchboard" element={<SwitchboardView />} />
        <Route path="/switchboard/:switchKey" element={<SwitchboardView />} />
        <Route path="*" element={<NotFoundView />} />
      </Routes>
    </AppShell>
    </>
  );
}

/**
 * `/emails/:id` used to open one sent email. The log lives inside Settings now
 * and selects with `?email=`, so the old link is translated rather than lost —
 * activity rows and notification emails still point at it.
 */
function LegacyEmailRedirect() {
  const { id } = useParams<{ id?: string }>();
  const target = id
    ? `/settings?tab=emails&email=${encodeURIComponent(id)}`
    : '/settings?tab=emails';
  return <Navigate to={target} replace />;
}
